// ─────────────────────────────────────────────────────────────────────────────
//  graphs/RadarChart.jsx
//
//  Self-contained:  transform  •  render
//  (Radar has no stacking or mixed units — no custom tooltip needed)
// ─────────────────────────────────────────────────────────────────────────────

import React from "react";
import ReactApexChart from "react-apexcharts";
import {
    ChartAnimationWrapper, ErrorMessage,
    getBaseChartConfig, getSeriesColors, getSingleUnit,
    formatNumberWithSuffix, formatYAxisSmart, sanitiseSeriesName,
    formatSeries, detectUnitField,
    BIOMEDICAL_WASTE_COLORS, DEFAULT_COLORS,
} from "../mainUnit";

// ─── TRANSFORM ────────────────────────────────────────────────────────────────
const transformData = (rawData, groupedField, title, dataSourceType) => {
    if (!rawData || rawData.length === 0) return null;

    const xAxisField   = groupedField?.[0];
    const stackByField = groupedField?.[1] || null;
    let series = [], categories = [], colorMap = {}, unitsMap = {};

    if (stackByField && xAxisField) {
        const groupedData = {}, stackValues = new Set();
        rawData.forEach(item => {
            const xVal     = item[xAxisField];
            const stackVal = item[stackByField];
            const value    = parseFloat(item.sum_value || item.min_value) || 0;
            stackValues.add(stackVal);
            if (!groupedData[stackVal]) groupedData[stackVal] = {};
            groupedData[stackVal][xVal] = (groupedData[stackVal][xVal] || 0) + value;
        });

        const totals = {};
        rawData.forEach(item => {
            const xVal  = item[xAxisField];
            const value = parseFloat(item.sum_value || item.min_value) || 0;
            totals[xVal] = (totals[xVal] || 0) + value;
        });
        categories = Object.keys(totals)
        const { unitsMap: detectedUnitsMap } = detectUnitField(rawData, xAxisField, stackByField);
        unitsMap = detectedUnitsMap;

        [...stackValues].forEach((stackVal, index) => {
            const color = BIOMEDICAL_WASTE_COLORS[stackVal] || DEFAULT_COLORS[index % DEFAULT_COLORS.length];
            colorMap[stackVal] = color;
            series.push({ name: String(stackVal), data: categories.map(xVal => groupedData[stackVal][xVal] || 0), color });
        });
    } else if (xAxisField) {
        const aggregated = {};
        rawData.forEach(item => {
            const label = item[xAxisField] || 'Unknown';
            aggregated[label] = (aggregated[label] || 0) + (parseFloat(item.sum_value || item.min_value) || 0);
            if (item.unit) unitsMap[label] = item.unit;
        });
        const labels = Object.keys(aggregated);
        labels.forEach((lbl, idx) => { colorMap[lbl] = BIOMEDICAL_WASTE_COLORS[lbl] || DEFAULT_COLORS[idx % DEFAULT_COLORS.length]; });
        categories = labels;
        series = [{ data: labels.map(lbl => aggregated[lbl]) }];
    }

    return {
        graphType: 'radar', dataSourceType, title: title || 'Analysis',
        categories, series: formatSeries(series),
        colors: DEFAULT_COLORS, colorMap, unitsMap,
    };
};

// ─── LEGEND FORMATTER ─────────────────────────────────────────────────────────
const buildLegendFormatter = (seriesArr, sharedUnit) => (seriesName) => {
    const clean = sanitiseSeriesName(seriesName);
    if (sharedUnit) return clean;
    const s = (seriesArr || []).find(x => x.name === seriesName);
    return s?.unit ? `${clean} (${s.unit})` : clean;
};

// ─── COMPONENT ────────────────────────────────────────────────────────────────
export const RadarChart = ({ chartData, chartOptions = {}, groupedField = [] }) => {
    const data = chartOptions.transform
        ? transformData(chartData?.data, groupedField, chartData?.title, chartData?.dataSourceType)
        : chartData;

    if (data?.errorMessage) return <ErrorMessage message={data.errorMessage} />;

    const seriesColors = getSeriesColors(data.series, data.colorMap, data.colors);
    const sharedUnit   = getSingleUnit(data.unitsMap);
    const baseConfig   = getBaseChartConfig('light', seriesColors, data, chartOptions.toolbarVisible !== false);

    const chartConfig = {
        ...baseConfig,
        chart:   { ...baseConfig.chart, type: 'radar', height: chartOptions.height || 450 },
        xaxis:   { categories: data.categories || [] },
        fill:    { opacity: 0.18 },
        stroke:  { width: 2 },
        markers: { size: 4, strokeWidth: 2, strokeColors: '#fff' },
        plotOptions: {
            radar: {
                size: 140,
                polygons: {
                    strokeColors: '#e2e8f0', strokeWidth: 1,
                    connectorColors: '#f1f5f9',
                    fill: { colors: ['#f8fafc', '#fff'] },
                },
            },
        },
        yaxis:  { labels: { formatter: formatYAxisSmart } },
        legend: { ...baseConfig.legend, formatter: buildLegendFormatter(data.series, sharedUnit) },
        tooltip: {
            ...baseConfig.tooltip,
            y: { formatter: (val) => { const f = formatNumberWithSuffix(val); return sharedUnit ? `${f} ${sharedUnit}` : f; }, title: { formatter: (n) => { const c = sanitiseSeriesName(n); return c ? `${c}:` : ''; } } },
        },
    };

    return (
        <ChartAnimationWrapper variant="zoomIn" duration={650}>
            <ReactApexChart options={chartConfig} series={data.series} type="radar" height={chartOptions.height || 450} />
        </ChartAnimationWrapper>
    );
};
